/**
 * A card order the operator arranged by hand, remembered between visits.
 *
 * The grid has a good automatic order - grouped by site, worst status first - and this
 * does not replace it so much as let one person overrule it. Someone watching a
 * particular pair of hosts through an incident wants them at the top together, and no
 * automatic rule can know which pair that is this week.
 *
 * **The stored order is a preference, not a source of truth.** It is a list of target
 * ids in localStorage, and the ids in it may be stale - a manual monitor was deleted, a
 * category was added, a host was renamed. So it is never rendered directly: it is
 * *applied to* the current default order by :func:`merge`, which keeps only the ids that
 * still exist and appends anything new. A new target therefore appears rather than being
 * silently dropped for not having been dragged yet, and a deleted one leaves nothing
 * behind. That is also why nothing here needs a migration or a version field: the worst
 * a nonsense entry can do is be ignored.
 *
 * Not synced to the server. It is one person's arrangement of their own screen, and
 * putting it in the backend would either make it everyone's or need a per-user store,
 * neither of which is what was asked for.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'whd.card-order'

function read(): string[] | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    return parsed.filter((entry): entry is string => typeof entry === 'string')
  } catch {
    // A private-mode browser, a disabled storage, or a half-written value. The grid
    // works without this, so a failure here is not worth a word to the operator.
    return null
  }
}

function write(order: string[] | null): void {
  try {
    if (order === null) window.localStorage.removeItem(STORAGE_KEY)
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(order))
  } catch {
    /* see read() */
  }
}

/**
 * The saved order, reconciled with what is actually on screen.
 *
 * Ids the operator arranged come first, in their arranged order; anything the default
 * has that the saved list does not is appended, keeping its default relative order. So
 * an arrangement survives a new target being added, and the new target is visible.
 */
function merge(custom: string[] | null, fallback: string[]): string[] {
  if (custom === null) return fallback
  const present = new Set(fallback)
  const placed = custom.filter((id) => present.has(id))
  const seen = new Set(placed)
  return [...placed, ...fallback.filter((id) => !seen.has(id))]
}

/** Move `id` to `toIndex`, closing the gap it left. */
function reorder(order: string[], id: string, toIndex: number): string[] {
  const from = order.indexOf(id)
  if (from < 0) return order
  const target = Math.max(0, Math.min(order.length - 1, toIndex))
  if (from === target) return order
  const next = [...order]
  next.splice(from, 1)
  next.splice(target, 0, id)
  return next
}

export interface CardOrder {
  /** The ids to render, in order. */
  order: string[]
  /** Whether this is a hand-arranged order rather than the automatic one. */
  isCustom: boolean
  /** Drop `dragId` onto the position `overId` currently occupies. */
  moveOnto: (dragId: string, overId: string) => void
  /** Nudge one card by `delta` places - the keyboard path, from the drag handle. */
  moveBy: (id: string, delta: number) => void
  /** Forget the arrangement and go back to the automatic order. */
  reset: () => void
}

export function useCardOrder(defaultOrder: string[]): CardOrder {
  const [custom, setCustom] = useState<string[] | null>(read)

  useEffect(() => {
    write(custom)
  }, [custom])

  const order = useMemo(() => merge(custom, defaultOrder), [custom, defaultOrder])

  // Every move writes the *whole* current order, not a patch: it pins the cards the
  // operator did not touch as well as the one they did. Otherwise the next status change
  // would reshuffle everything around the one card they had placed, which reads as the
  // drag not having worked.
  const moveTo = useCallback(
    (id: string, toIndex: number) => setCustom(reorder(order, id, toIndex)),
    [order],
  )

  const moveOnto = useCallback(
    (dragId: string, overId: string) => {
      const to = order.indexOf(overId)
      if (to >= 0 && dragId !== overId) moveTo(dragId, to)
    },
    [order, moveTo],
  )

  const moveBy = useCallback(
    (id: string, delta: number) => {
      const from = order.indexOf(id)
      if (from >= 0) moveTo(id, from + delta)
    },
    [order, moveTo],
  )

  const reset = useCallback(() => setCustom(null), [])

  return { order, isCustom: custom !== null, moveOnto, moveBy, reset }
}
